import { useAuth } from "@clerk/clerk-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import { useState } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import ShieldLockIcon from "../icons/ShieldLock";

// TODO: Global Redemption Link, Hold from Shopify, Set to Billback and Email thank-you notes to
// sender come back as rows here once their tickets make them persist.
const FREEZE_ROW = {
  id: "freezeGiftSelection",
  label: "Freeze Gift Selection",
  hint: "New products synced from Shopify won't be added to this campaign.",
};

function ToggleSwitch({
  id,
  checked,
  onChange,
  labelledBy,
  describedBy,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  labelledBy: string;
  describedBy: string;
}) {
  return (
    <label
      htmlFor={id}
      className="relative shrink-0 cursor-pointer"
      style={{ width: 58, height: 38 }}
    >
      <input
        type="checkbox"
        // biome-ignore lint/a11y/useAriaPropsForRole: a native checkbox exposes its own checked state; ARIA in HTML forbids aria-checked on it.
        role="switch"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        className="sr-only peer"
      />
      {/* Track: 34×14, centered in 58×38 touch area */}
      <span
        className="absolute rounded-full bg-smoke peer-checked:bg-dark-dust transition-colors duration-200"
        style={{ left: 12, top: 12, width: 34, height: 14 }}
      />
      {/* Thumb: 20×20, overhangs track by 3px on each side, translates 20px when checked */}
      <span
        className="absolute w-5 h-5 rounded-full bg-white peer-checked:translate-x-5 transition-transform duration-200"
        style={{ left: 9, top: 9, boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }}
      />
    </label>
  );
}

/**
 * Campaign-builder staff panel. Self-gating: the permission check lives here, not at the call
 * site, so adding <GfgStaffOnly /> to another step cannot leak it to senders.
 *
 * CAMPAIGN_BUILDER/view at scope "all" is held only by GFG_STANDARD_ADMIN and GFG_SUPER_ADMIN.
 * Client Admin and Org View Only hold the same subject at "org", Manager and Campaign Manager
 * fall to the "group" default, and View Only / GFG Finance do not hold it at all. Gating on
 * CAMPAIGN/edit instead would wrongly exclude GFG_STANDARD_ADMIN, whose CAMPAIGN scope is
 * "group" — only the super admin holds that one at "all".
 */
export default function GfgStaffOnly() {
  const [isExpanded, setIsExpanded] = useState(false);
  const { actor } = useAuth();
  const { freezeGiftSelection, setFreezeGiftSelection } = useCampaignBuilder();

  // GFG staff reach the builder by impersonating the client, whose session is not GFG.
  if (actor) return panel();
  return (
    <PermissionGate action="view" subject="CAMPAIGN_BUILDER" scope="all">
      {panel()}
    </PermissionGate>
  );

  function panel() {
    const labelId = `${FREEZE_ROW.id}-label`;
    const hintId = `${FREEZE_ROW.id}-hint`;
    return (
      <div
        className="mt-8 rounded-card bg-white"
        style={{
          border: "1px solid var(--color-dark-dust)",
          boxShadow: "0 4px 8px rgba(0,0,0,0.06)",
        }}
      >
        {/* Header */}
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          aria-expanded={isExpanded}
          className="w-full flex items-center gap-6 px-5 py-5 text-left hover:bg-clear-dust transition-colors rounded-card"
        >
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: "var(--color-good-wine-4)" }}
          >
            <ShieldLockIcon sx={{ fontSize: 36, color: "var(--color-good-wine)" }} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <p className="font-crimson text-[18px] text-rodeo-black leading-none">
                GFG Staff Only
              </p>
              {freezeGiftSelection && (
                <span
                  className="inline-flex items-center px-2.5 py-1 rounded-pill font-lato text-[9px] font-bold uppercase tracking-[1.2px] whitespace-nowrap"
                  style={{
                    background: "var(--color-good-wine-4)",
                    color: "var(--color-good-wine)",
                  }}
                >
                  Frozen
                </span>
              )}
            </div>
            <p className="font-lato text-[12px] text-graphite mt-1">
              Hidden from senders — visible only when logged in as GFG admin or account manager
            </p>
          </div>

          <ExpandMoreIcon
            sx={{
              fontSize: 24,
              color: "var(--color-graphite)",
              transition: "transform 0.25s ease",
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              flexShrink: 0,
            }}
          />
        </button>

        {/* Body */}
        <Collapse in={isExpanded}>
          <div style={{ height: 1, background: "var(--color-card-border)" }} />
          <div className="px-5 pb-6 pt-1">
            <div className="flex items-center gap-6 py-4">
              <div className="flex-1 min-w-0">
                <p id={labelId} className="font-lato text-lg font-semibold text-rodeo-black">
                  {FREEZE_ROW.label}
                </p>
                <p id={hintId} className="font-lato text-base text-graphite mt-1.5">
                  {FREEZE_ROW.hint}
                </p>
                <p
                  role="status"
                  className={`font-lato text-base font-semibold text-good-wine ${freezeGiftSelection ? "mt-1.5" : ""}`}
                >
                  {freezeGiftSelection ? "Gift selection is frozen." : ""}
                </p>
              </div>
              <ToggleSwitch
                id={FREEZE_ROW.id}
                checked={freezeGiftSelection}
                onChange={setFreezeGiftSelection}
                labelledBy={labelId}
                describedBy={hintId}
              />
            </div>
          </div>
        </Collapse>
      </div>
    );
  }
}
